import { useId, useState, type FormEvent } from 'react';
import { Button } from '@shared/ui/Button';
import { RdPositionPicker } from './RdPositionPicker';
import { Card, Field, Grid, Input, Row, Select, Textarea } from '@widgets/ksp-workspace/parts';

export interface FormField {
  key: string;
  label: string;
  type?:
    'text' | 'date' | 'datetime-local' | 'time' | 'number' | 'textarea' | 'select' | 'rd-position';
  required?: boolean;
  value?: string;
  options?: { value: string; label: string }[];
  help?: string;
  clears?: string[];
  projectId?: string;
}
export interface FormSpec {
  /** Уникален для каждого открытия формы: значения одной строки не переходят в другую. */
  id?: number;
  title: string;
  fields: FormField[];
  submit?: string;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}
export function ActionForm({
  spec,
  busy,
  onClose,
}: {
  spec: FormSpec;
  busy: boolean;
  onClose: () => void;
}) {
  const formId = useId();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(spec.fields.map((f) => [f.key, f.value ?? ''])),
  );
  const [validationError, setValidationError] = useState('');
  const changeValue = (field: FormField, value: string) =>
    setValues((prev) => ({
      ...prev,
      ...Object.fromEntries((field.clears ?? []).map((key) => [key, ''])),
      [field.key]: value,
    }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const missing = spec.fields.find((field) => field.required && !values[field.key]?.trim());
    if (missing) {
      setValidationError(`Заполните поле «${missing.label}».`);
      return;
    }
    setValidationError('');
    await spec.onSubmit(values);
  };
  return (
    <Card>
      <h3>{spec.title}</h3>
      <form onSubmit={submit}>
        <Grid>
          {spec.fields.map((field) => (
            <Field as="div" key={field.key}>
              <label htmlFor={`${formId}-${field.key}`}>{field.label}</label>
              {field.type === 'rd-position' ? (
                <RdPositionPicker
                  key={values.field_site_id ?? 'project'}
                  projectId={field.projectId ?? ''}
                  siteId={values.field_site_id ?? ''}
                  value={values[field.key]}
                  id={`${formId}-${field.key}`}
                  disabled={busy}
                  onChange={(value) => changeValue(field, value)}
                />
              ) : field.type === 'select' ? (
                <Select
                  id={`${formId}-${field.key}`}
                  value={values[field.key]}
                  required={field.required}
                  onChange={(event) => changeValue(field, event.target.value)}
                >
                  <option value="">Выберите</option>
                  {field.options?.map((option) => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              ) : field.type === 'textarea' ? (
                <Textarea
                  id={`${formId}-${field.key}`}
                  required={field.required}
                  value={values[field.key]}
                  onChange={(event) => changeValue(field, event.target.value)}
                />
              ) : (
                <Input
                  id={`${formId}-${field.key}`}
                  type={field.type ?? 'text'}
                  step={field.type === 'number' ? 1 : undefined}
                  required={field.required}
                  value={values[field.key]}
                  onChange={(event) => changeValue(field, event.target.value)}
                />
              )}
              {field.help && <small>{field.help}</small>}
            </Field>
          ))}
        </Grid>
        {validationError && <small role="alert">{validationError}</small>}
        <Row>
          <Button type="submit" disabled={busy}>
            {spec.submit ?? 'Применить'}
          </Button>
          <Button type="button" $variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
        </Row>
      </form>
    </Card>
  );
}
